import React from 'react';
import { useTranslation } from 'react-i18next';

export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  children: React.ReactNode;
}

/**
 * Escala de botones del panel (admin denso):
 * - sm  → toolbars / acciones de fila (~32px)
 * - md  → default CTAs y footers de modal (~36px)
 * - lg  → formularios full-width (login)
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  disabled,
  className = '',
  children,
  ...props
}) => {
  const { t } = useTranslation();

  const baseClasses =
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed';

  const variantClasses: Record<ButtonVariant, string> = {
    primary: 'bg-teal-600 text-white hover:bg-teal-700 focus:ring-teal-500',
    secondary:
      'bg-white text-slate-700 hover:bg-slate-50 focus:ring-teal-500 border border-slate-200 dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
    ghost:
      'bg-transparent text-slate-700 hover:bg-slate-100 focus:ring-teal-500 dark:text-slate-300 dark:hover:bg-gray-800',
  };

  const sizeClasses: Record<ButtonSize, string> = {
    sm: 'h-8 px-2.5 text-xs leading-none',
    md: 'h-9 px-3 text-sm leading-none',
    lg: 'h-10 px-4 text-sm leading-none',
  };

  const spinnerSize = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <>
          <svg
            className={`animate-spin ${spinnerSize}`}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          {t('common.loading')}
        </>
      ) : (
        children
      )}
    </button>
  );
};
